import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector, Type } from '@angular/core';
import { CustomToastComponent } from '../components/custom-toast/custom-toast.component';

/**
 * Service for displaying toast notifications
 * 
 * Usage examples:
 * 
 * // Regular error toast (top-right corner)
 * this.customToast.showError('Operation failed', 'Error');
 * 
 * // Modal error toast (center of screen)
 * this.customToast.showErrorModal('Critical error occurred', 'Error');
 * 
 * // Success toast
 * this.customToast.showSuccess('Operation completed successfully', 'Success');
 * 
 * // Warning toast
 * this.customToast.showWarning('Please check your input', 'Warning');
 */
@Injectable({
  providedIn: 'root'
})
export class CustomToastService {
  private activeToasts: Array<{
    component: CustomToastComponent;
    timeout: number;
  }> = [];

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    document.addEventListener('toastClose', ((event: CustomEvent) => {
      this.removeToast(event.detail);
    }) as EventListener);
  }

  showError(message: string, title: string = 'Error', duration: number = 5000): void {
    this.showToast(message, title, duration, 'error');
  }

  showSuccess(message: string, title: string = 'Success', duration: number = 5000): void {
    this.showToast(message, title, duration, 'success');
  }

  showWarning(message: string, title: string = 'Warning', duration: number = 5000): void {
    this.showToast(message, title, duration, 'warning');
  }

  showInfo(message: string, title: string = 'Info', duration: number = 5000): void {
    this.showToast(message, title, duration, 'info');
  }

  /**
   * Shows an error toast as a modal in the center of the screen
   * @param message The error message to display
   * @param title The title of the error toast (default: 'Error')
   */
  showErrorModal(message: string, title: string = 'Error'): void {
    this.showModalToast(message, title, 'error');
  }

  /**
   * Shows a warning toast as a modal in the center of the screen
   * @param message The warning message to display
   * @param title The title of the warning toast (default: 'Warning')
   */
  showWarningModal(message: string, title: string = 'Warning'): void {
    this.showModalToast(message, title, 'warning');
  }

  private showToast(message: string, title: string, duration: number, type: 'error' | 'success' | 'warning' | 'info'): void {
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(CustomToastComponent)
      .create(this.injector);

    const instance = componentRef.instance;
    instance.message = message;
    instance.title = title;
    instance.duration = duration;
    instance.type = type;
    instance.isModal = false;

    const element = instance.element;
    document.body.appendChild(element);
    this.appRef.attachView(componentRef.hostView);

    const timeout = window.setTimeout(() => {
      this.removeToast(instance);
    }, duration);

    this.activeToasts.push({ component: instance, timeout });
  }

  private showModalToast(message: string, title: string, type: 'error' | 'success' | 'warning' | 'info'): void {
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(CustomToastComponent)
      .create(this.injector);

    const instance = componentRef.instance;
    instance.message = message;
    instance.title = title;
    instance.duration = 0; // No auto-close for modal
    instance.type = type;
    instance.isModal = true;

    const element = instance.element;
    document.body.appendChild(element);
    this.appRef.attachView(componentRef.hostView);

    // No timeout for modal toasts - they must be closed manually
    this.activeToasts.push({ component: instance, timeout: 0 });
  }

  private removeToast(toast: CustomToastComponent): void {
    const index = this.activeToasts.findIndex(t => t.component === toast);
    if (index > -1) {
      const { timeout } = this.activeToasts[index];
      if (timeout) {
        window.clearTimeout(timeout);
      }
      
      // Remove the toast element
      document.body.removeChild(toast.element);
      
      // If it's a modal toast, also remove the backdrop
      if (toast.isModal) {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          document.body.removeChild(backdrop);
        }
      }
      
      this.activeToasts.splice(index, 1);
    }
  }

  clearAll(): void {
    this.activeToasts.forEach(({ component, timeout }) => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
      document.body.removeChild(component.element);
      
      // If it's a modal toast, also remove the backdrop
      if (component.isModal) {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          document.body.removeChild(backdrop);
        }
      }
    });
    this.activeToasts = [];
  }
} 